import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { OrganisationWithCounts } from '../../../../../../backend/src/types/types';
import { Breadcrumb } from '../../../components/breadcrumb';
import { ScrollableTable } from '../../../components/table/scrollable-table';
import { useGetAllOrganisations } from '@/api/queries/useGetOrganisations';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { CellWithCopy } from '@/new/components/table/table-cell-with-copy';

const fallbackTableData: OrganisationWithCounts[] = [];
const columnHelper = createColumnHelper<OrganisationWithCounts>();

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Organisation Name</span>,
    cell: (info) => <span className="line-clamp-1 min-w-48 break-all">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'manageOrgs',
    header: () => <span>Manage Organisation</span>,
    cell: (props) => (
      <Button size="sm" asChild>
        <Link to={`/admin/organisations/${props.row.original.id}`}>Manage Organisation</Link>
      </Button>
    ),
  }),
  columnHelper.accessor('id', {
    header: () => <span>Organisation Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-24 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('userCount', {
    header: () => <span>No. of Users</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'manageUsers',
    header: () => <span>Manage Users</span>,
    cell: (props) => (
      <Button size="sm" asChild>
        <Link to={`/admin/organisations/${props.row.original.id}/users`}>Manage Users</Link>
      </Button>
    ),
  }),
  columnHelper.accessor('matterCount', {
    header: () => <span>No. of Matters</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'manageRuns',
    header: () => <span>Runs</span>,
    cell: (props) => (
      <Button size="sm" asChild>
        <Link to={`/admin/organisations/${props.row.original.id}/runs`}>Manage Runs</Link>
      </Button>
    ),
  }),
  columnHelper.display({
    id: 'manageAuditTrail',
    header: () => <span>Audit Trail</span>,
    cell: (props) => (
      <Button size="sm" asChild>
        <Link to={`/admin/organisations/${props.row.original.id}/audit-trail`}>Audit trail</Link>
      </Button>
    ),
  }),
];

export function Admin() {
  useGlobalAdminCheck();

  const { data: organisations } = useGetAllOrganisations();

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: organisations ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'userCount',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb items={[{ href: '/admin/organisations', label: 'Admin', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Admin" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable
        table={table}
        plural="organisations"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
    </>
  );
}
